import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  AppDispatch,
  AlertsRequests,
  SegmentRequestActions,
  NotificationsRequests,
  AppState,
  segmentsSelectors,
  SettingsRequestActions,
  settingsSelectors,
  fullstorySelectors,
  FullstoryActions,
} from '@fullstory/redux';
import { AlertAndKey, DefaultMaxAlerts } from '@fullstory/client-api';

const { getOrgId } = fullstorySelectors;
const { getSegmentsById, getSegmentsLoading } = segmentsSelectors;
const { rootSelector: settingsSelector } = settingsSelectors;

export enum segmentedControls {
  notifications = 'notifications',
  alerts = 'alerts',
}

export const useAlertNotifications = (visible: boolean) => {
  const dispatch = useDispatch<AppDispatch>();
  const lastSeen = useSelector(fullstorySelectors.getNotificationsLastSeen);
  const [notification, setNotification] = useState(false);

  // on mount, fetch notifications
  useEffect(() => {
    dispatch(
      NotificationsRequests.getOverlayNotifications({
        Limit: 50,
      }),
    );
  }, [dispatch]);

  // when visibility changes, update last seen locally and on the server
  useEffect(() => {
    if (visible) {
      const now = new Date().getTime();
      dispatch(FullstoryActions.setNotificationsLastSeen(now));
      dispatch(NotificationsRequests.updateNotificationsSeen(now));
      setNotification(false);
    }
  }, [dispatch, visible]);

  const notificationRequestState = useSelector(
    (state: AppState) => state.alerts.notificationsOverlay,
  );
  useEffect(() => {
    if (
      !visible &&
      (notificationRequestState.data?.Notifications?.filter(
        n => lastSeen < n.Notification.CreatedAt,
      )?.length ?? 0) > 0
    ) {
      setNotification(true);
    }
  }, [notificationRequestState.data, lastSeen, visible]);

  return { lastSeen, notification };
};

export const useAlertBell = () => {
  const [visible, setVisible] = useState(false);
  const { lastSeen, notification } = useAlertNotifications(visible);

  return {
    setVisible,
    visible,
    lastSeen,
    notification,
  };
};

export const useAlertOverlay = (email: string) => {
  const dispatch = useDispatch<AppDispatch>();
  const intl = useIntl();
  const orgId: string = useSelector(getOrgId);

  useEffect(() => {
    dispatch(
      NotificationsRequests.getOverlayNotifications({
        Limit: 50,
      }),
    );
    dispatch(SegmentRequestActions.getAllSegments(orgId, false));
    // empty string in getAlerts tells the server to get all alerts
    dispatch(AlertsRequests.getOverlayAlerts('', undefined, true));
    dispatch(SettingsRequestActions.getSettings());
  }, [dispatch, orgId]);

  const {
    notificationRequestState,
    segments,
    segmentsLoading,
    alertsRequestState,
    settingsRequestState,
    lastKnownSegment,
  } = useSelector(
    (state: AppState) => ({
      alertsRequestState: state.alerts.alertsOverlay,
      notificationRequestState: state.alerts.notificationsOverlay,
      segments: getSegmentsById(state),
      segmentsLoading: getSegmentsLoading(state),
      settingsRequestState: settingsSelector(state).settings,
      lastKnownSegment: state.segments.lastKnownSegment,
    }),
    shallowEqual,
  );

  const alertLimit = settingsRequestState.data?.plan?.alerts ?? DefaultMaxAlerts;
  const alertLength = alertsRequestState?.data?.Alerts?.length ?? 0;
  const alertLabel = intl.formatMessage(
    {
      defaultMessage:
        '{hasAlerts, select, true {Alerts ({alertLength}/{alertLimit})} other {Alerts}}',
      id: 'FynOCL',
    },
    { hasAlerts: Boolean(alertLimit && alertLength), alertLength, alertLimit },
  );

  const segmentControlOptions = useMemo(
    (): { id: string; label: string }[] => [
      {
        id: segmentedControls.notifications,
        label: intl.formatMessage({ defaultMessage: 'Notifications', id: 'NAidKb' }),
      },
      {
        id: segmentedControls.alerts,
        label: alertLabel,
      },
    ],
    [intl, alertLabel],
  );

  const { myAlerts, teamAlerts } = useMemo(() => {
    const myAlerts: AlertAndKey[] = [];
    const teamAlerts: AlertAndKey[] = [];
    alertsRequestState?.data?.Alerts?.forEach(alert => {
      if (alert.Alert.CreatedBy === email) myAlerts.push(alert);
      else teamAlerts.push(alert);
    });
    return {
      myAlerts,
      teamAlerts,
    };
  }, [alertsRequestState, email]);

  return {
    myAlerts,
    teamAlerts,
    notifications: notificationRequestState?.data,
    segments,
    loading:
      segmentsLoading ||
      notificationRequestState.loading ||
      alertsRequestState.loading ||
      settingsRequestState.loading,
    segmentControlOptions,
    lastKnownSegment,
    orgId,
    hideNotifications: true,
    alertLabel,
    alertLimit,
    alertLength,
  };
};
